import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useDistrict } from "../lib/district-context";
import BrandLogo from "../components/shared/BrandLogo";
import FormField from "../components/forms/FormField";
import { toast } from "@/lib/toast";
import { Church, User, MapPin, Clock } from "lucide-react";
import { useIsMounted } from "../hooks/useIsMounted";
import { logger } from "@/lib/logger";

export default function CreateDistrict() {
  const nav = useNavigate();
  const { refreshProfile } = useDistrict();

  const [name,    setName]    = useState("");
  const [pastor,  setPastor]  = useState("");
  const [field,   setField]   = useState("");
  const [loading, setLoading] = useState(false);
  const [waiting, setWaiting] = useState(false);

  const isMounted = useIsMounted();

  const handleCreate = async () => {
    if (loading) return;

    if (!name.trim()) {
      toast.error("El nombre del distrito es obligatorio");
      return;
    }

    setLoading(true);

    try {
      // bootstrap_district is a security-definer RPC that atomically:
      // 1. Validates the caller is approved/admin/leader
      // 2. INSERTs the district
      // 3. UPDATEs profiles.district_id in the same transaction
      // This bypasses the RLS bootstrap deadlock (approved users can't INSERT
      // into districts directly because the policy requires admin status).
      const { data: district, error } = await supabase.rpc("bootstrap_district", {
        p_name:        name.trim(),
        p_pastor_name: pastor.trim(),
        p_field_name:  field.trim(),
      });

      if (error) throw error;
      if (!district?.id) throw new Error("No district returned");

      toast.success("Distrito creado");

      // Recarga el perfil en el contexto ANTES de navegar. bootstrap_district ya
      // dejó profiles.district_id actualizado en la base, pero DistrictProvider
      // sigue con el valor viejo en memoria (district_id null) hasta que algo se
      // lo pida. Sin este await, nav("/") monta RequireDistrict con el contexto
      // viejo, que rebota de vuelta a /create-district aunque todo ya esté bien
      // en la base — mismo bug para un admin que se soltó y para un pastor nuevo.
      await refreshProfile?.();

      nav("/", { replace: true });

    } catch (err) {
      logger.error("[CreateDistrict] error:", err);
      if (err?.message?.includes("already configured")) {
        // Pastor was transferred — they already have a district, just waiting
        // for the app to reload with the updated profile context.
        if (isMounted()) setWaiting(true);
        return;
      }
      const msg = err?.message?.includes("Not authorized")
        ? "Tu cuenta no está autorizada para crear un distrito"
        : "No se pudo crear el distrito";
      toast.error(msg);
    } finally {
      if (isMounted()) setLoading(false);
    }
  };

  if (waiting) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-gradient-to-br from-[#eef5fb] to-white dark:from-slate-950 dark:to-slate-900 px-4">
        <div className="w-full max-w-sm text-center space-y-4">
          <div className="mx-auto h-16 w-16 rounded-full bg-amber-50 dark:bg-amber-900/20 flex items-center justify-center">
            <Clock size={28} className="text-amber-500 dark:text-amber-400" />
          </div>
          <h2 className="text-[18px] font-semibold text-slate-800 dark:text-slate-100">
            En espera de asignación
          </h2>
          <p className="text-[14px] text-slate-500 dark:text-slate-400 leading-relaxed">
            Tu perfil ya está vinculado a un distrito. Cierra sesión y vuelve a entrar para acceder.
          </p>
          <button
            onClick={() => nav("/", { replace: true })}
            className="w-full bg-brand text-white py-3 rounded-[14px] text-[15px] font-semibold transition-all duration-200 active:scale-[0.98]"
          >
            Ir al inicio
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] flex items-center justify-center bg-gradient-to-br from-[#eef5fb] to-white dark:from-slate-950 dark:to-slate-900 px-4">

      <div className="w-full max-w-sm space-y-6">

        {/* 🔥 LOGO + IDENTIDAD */}
        <div className="text-center space-y-2">

          <BrandLogo variant="auto" className="mx-auto w-20 h-20 object-contain" />

          <h1 className="text-lg font-semibold text-slate-800 dark:text-slate-100">
            Kéves App
          </h1>

          <p className="text-xs text-slate-500 dark:text-slate-400">
            Configura tu distrito para comenzar
          </p>
        </div>

        {/* 🧾 FORM */}
        <div className="bg-white dark:bg-slate-900 rounded-[20px] p-5 border border-slate-100 dark:border-slate-800 shadow-sm space-y-4">

          <FormField
            icon={<Church size={18} />}
            placeholder="Nombre del distrito"
            value={name}
            onChange={setName}
          />

          <FormField
            icon={<User size={18} />}
            placeholder="Pastor"
            value={pastor}
            onChange={setPastor}
          />

          <FormField
            icon={<MapPin size={18} />}
            placeholder="Campo / Asociación"
            value={field}
            onChange={setField}
          />

          <button
            onClick={handleCreate}
            disabled={loading}
            className="w-full bg-brand text-white py-3 rounded-[14px] text-[16px] font-semibold transition-all duration-200 ease-out hover:shadow-md active:scale-[0.98] disabled:opacity-50"
          >
            {loading ? "Creando..." : "Crear distrito"}
          </button>

        </div>

      </div>
    </div>
  );
}
